import { useEffect, useState, useMemo } from "react";
import {
  Bell,
  Calendar,
  ChevronDown,
  Sparkles,
  Search,
  Info,
  Clock,
  Zap,
  Lock,
  Globe,
  MapPin,
  Copy,
  Eye,
  AlertTriangle,
  FileText,
  Activity
} from "lucide-react";
import Skeleton from "../../components/Skeleton";
import ForumRiskTable from "../../components/ForumRiskTable";
import ScraperForumStatus from "../../components/ScraperForumStatus";
import { toast } from "sonner";
import { useAuth } from "../../auth/AuthContext";

import {
  fetchStatusOverview,
  fetchWhatsNew,
  fetchForumRisk,
} from "../../api/status";
import { fetchPublicIP, fetchTorIP } from "../../api/network";

import type { StatusOverview, WhatsNew, ForumRisk } from "../../api/status";
import type { IPInfo } from "../../api/network";
import type { Leak } from "../../types/leak";
import { getPendingApprovals, getActiveEngagements } from "../../api/engagement";import { useOutletContext } from "react-router-dom";

export default function AdminDashboard() {
  const { user } = useAuth();
  
  const { theme } = useOutletContext<{ theme: "dark" | "light" }>();

  const [stats, setStats] = useState<StatusOverview | null>(null);
  const [whatsNew, setWhatsNew] = useState<WhatsNew | null>(null);
  const [forumRisk, setForumRisk] = useState<ForumRisk[]>([]);
  const [publicIP, setPublicIP] = useState<IPInfo | undefined>();
  const [torIP, setTorIP] = useState<IPInfo | undefined>();
  const [loading, setLoading] = useState(true);
  const [pendingLeaks, setPendingLeaks] = useState<Leak[]>([]);
  const [activeLeaks, setActiveLeaks] = useState<Leak[]>([]);

  // Interactive states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTimeframe, setSelectedTimeframe] = useState("Monthly");
  const [selectedSegment, setSelectedSegment] = useState("All Segment");
  const [selectedClientFilter, setSelectedClientFilter] = useState("All active");
  
  // Tabs for Engagement Queue
  const [eqTab, setEqTab] = useState<"pending" | "active">("pending");

  const distributionData = useMemo(() => {
    const raw = stats?.leaks_per_data_type || {};
    const categories = [
      { key: "Database", label: "Databases", color: "#2563eb" },
      { key: "Credentials", label: "Credentials", color: "#7c3aed" },
      { key: "PII", label: "PII", color: "#16a34a" },
      { key: "Financial", label: "Financial", color: "#ca8a04" },
      { key: "Government", label: "Government", color: "#ea580c" },
    ];
    
    const categoryCounts = categories.map(c => ({
      ...c,
      count: raw[c.key] || 0
    }));

    const totalLeaks = stats?.total_leaks || 0;
    const mappedSum = categoryCounts.reduce((sum, c) => sum + c.count, 0);
    const otherCount = Math.max(0, totalLeaks - mappedSum);

    const allCategories = [
      ...categoryCounts,
      { key: "Other", label: "Others", color: "#6b7280", count: otherCount }
    ].filter(c => c.count > 0 || c.key !== "Other");

    const sumAll = allCategories.reduce((sum, c) => sum + c.count, 0) || 1;
    let currentOffset = 0;
    const circ = 2 * Math.PI * 45; // ~282.74

    const list = allCategories.map(c => {
      const pct = (c.count / sumAll);
      const dashOffset = currentOffset;
      currentOffset -= pct * circ;
      return {
        ...c,
        pct: pct * 100,
        dashArray: `${pct * circ} ${circ}`,
        dashOffset: dashOffset
      };
    });

    return {
      total: sumAll,
      list
    };
  }, [stats]);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, w, f, pip, tip] = await Promise.allSettled([
          fetchStatusOverview(),
          fetchWhatsNew(),
          fetchForumRisk(),
          fetchPublicIP(),
          fetchTorIP(),
        ]);
        if (s.status === "fulfilled") setStats(s.value);
        if (w.status === "fulfilled") setWhatsNew(w.value);
        if (f.status === "fulfilled") setForumRisk(f.value);
        if (pip.status === "fulfilled") setPublicIP(pip.value);
        if (tip.status === "fulfilled") setTorIP(tip.value);

        try {
          const [pRes, aRes] = await Promise.allSettled([
            getPendingApprovals(),
            getActiveEngagements(),
          ]);
          if (pRes.status === "fulfilled") setPendingLeaks(pRes.value.results);
          if (aRes.status === "fulfilled") setActiveLeaks(aRes.value.results);
        } catch (err) {
          console.debug("Failed to load secondary stats", err);
        }
      } catch (e) {
        console.error("Dashboard load failed", e);
      } finally {
        loadIPsRepeatedly();
        setLoading(false);
      }
    };
    load();
  }, []);

  const loadIPsRepeatedly = () => {
    setInterval(async () => {
      try {
        const [pip, tip] = await Promise.allSettled([fetchPublicIP(), fetchTorIP()]);
        if (pip.status === "fulfilled") setPublicIP(pip.value);
        if (tip.status === "fulfilled") setTorIP(tip.value);
      } catch (err) {
        console.debug("Failed to update status IPs", err);
      }
    }, 15000);
  };

  const handleSearch = () => {
    toast.success(`Search completed for: "${searchQuery}"`);
  };

  const handleAIAssistant = () => {
    toast.info("Threat Intelligence AI Assistant is generating summary...", {
      description: `Analyzing recent ingestion speed (last 24h: ${whatsNew?.new_last_24h ?? 0} new leaks) and network nodes.`,
      duration: 4000,
    });
  };


  const handleCopyIP = (ip: string) => {
    navigator.clipboard.writeText(ip);
    toast.success("IP address copied to clipboard!");
  };

  if (loading || !stats) return <Skeleton rows={8} className="mt-8" />;

  // Map stat cards
  const statCards = [
    { title: "Total Leaks", value: stats.total_leaks, subtitle: "Indexed entries", color: "#a855f7" },
    { title: "Hidden Leaks", value: stats.hidden_leaks, subtitle: "Encrypted entries", color: "#a855f7" },
    { title: "Visible Leaks", value: stats.visible_leaks, subtitle: "Exposed publicly", color: "#3b82f6" },
    { title: "Failed Jobs", value: stats.failed_jobs ?? 0, subtitle: "Error logs", color: "#ef4444" },
    { title: "Retry Jobs", value: stats.retry_jobs ?? 0, subtitle: "Queue buffer", color: "#f59e0b" },
  ];

  const currentLeaks = eqTab === "pending" ? pendingLeaks : activeLeaks;
  const filteredLeaks = currentLeaks.filter(leak => {
    const q = searchQuery.toLowerCase();
    return (
      (leak.client_name || "").toLowerCase().includes(q) ||
      (leak.forum || "").toLowerCase().includes(q) ||
      (leak.thread_title || leak.title || "").toLowerCase().includes(q)
    );
  });

  const isDark = theme === "dark";
  const themeVars = isDark ? {
    bg: "#080a14",
    cardBg: "#101222",
    subCardBg: "#15172c",
    cardBorder: "#1a1c32",
    textMain: "#ffffff",
    textMuted: "rgba(255, 255, 255, 0.4)",
    textSubtle: "rgba(255, 255, 255, 0.6)",
    innerBorder: "rgba(255, 255, 255, 0.03)",
    shadow: "0 10px 30px rgba(0, 0, 0, 0.4)",
    inputBg: "rgba(255, 255, 255, 0.04)",
    inputBorder: "rgba(255, 255, 255, 0.08)",
    inputText: "#ffffff",
    tabActiveBg: "rgba(255, 255, 255, 0.08)",
    tabActiveText: "#ffffff",
    tabInactiveText: "rgba(255, 255, 255, 0.5)",
    rowHoverBg: "rgba(255, 255, 255, 0.02)",
    donutCenterText: "#ffffff",
    donutCenterSubText: "rgba(255, 255, 255, 0.4)",
    theadBg: "rgba(255, 255, 255, 0.06)",
    tableRowBorder: "rgba(255, 255, 255, 0.03)"
  } : {
    bg: "#f4f6fa",
    cardBg: "#ffffff",
    subCardBg: "#f8fafc",
    cardBorder: "#cbd5e1",
    textMain: "#0f172a",
    textMuted: "#64748b",
    textSubtle: "#334155",
    innerBorder: "#cbd5e1",
    shadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
    inputBg: "#ffffff",
    inputBorder: "#cbd5e1",
    inputText: "#0f172a",
    tabActiveBg: "#cbd5e1",
    tabActiveText: "#0f172a",
    tabInactiveText: "#64748b",
    rowHoverBg: "rgba(0, 0, 0, 0.03)",
    donutCenterText: "#0f172a",
    donutCenterSubText: "#64748b",
    theadBg: "rgba(0, 0, 0, 0.05)",
    tableRowBorder: "#e2e8f0"
  };

  return (
    <div className="admin-db-container animate-enter">
      {/* Dynamic override of GlobalHeader top-bar and color scheme mapping */}
      <style dangerouslySetInnerHTML={{__html: `
        .top-bar { display: none !important; }
        
        :root {
          --db-bg: ${themeVars.bg};
          --db-card-bg: ${themeVars.cardBg};
          --db-sub-card-bg: ${themeVars.subCardBg};
          --db-card-border: ${themeVars.cardBorder};
          --db-text-main: ${themeVars.textMain};
          --db-text-muted: ${themeVars.textMuted};
          --db-text-subtle: ${themeVars.textSubtle};
          --db-inner-border: ${themeVars.innerBorder};
          --db-shadow: ${themeVars.shadow};
          --db-input-bg: ${themeVars.inputBg};
          --db-input-border: ${themeVars.inputBorder};
          --db-input-text: ${themeVars.inputText};
          --db-tab-active-bg: ${themeVars.tabActiveBg};
          --db-tab-active-text: ${themeVars.tabActiveText};
          --db-tab-inactive-text: ${themeVars.tabInactiveText};
          --db-row-hover-bg: ${themeVars.rowHoverBg};
          --db-donut-center-text: ${themeVars.donutCenterText};
          --db-donut-center-subtext: ${themeVars.donutCenterSubText};
          --db-thead-bg: ${themeVars.theadBg};
          --db-table-row-border: ${themeVars.tableRowBorder};
        }

        .admin-db-container {
          background-color: var(--db-bg) !important;
          color: var(--db-text-main) !important;
        }

        .db-card-modern {
          background: var(--db-card-bg) !important;
          border: 1px solid var(--db-card-border) !important;
          box-shadow: var(--db-shadow) !important;
        }

        .stat-card-mod {
          background: var(--db-card-bg) !important;
          border: 1px solid var(--db-card-border) !important;
        }

        .net-sub-card {
          background: var(--db-sub-card-bg) !important;
          border: 1px solid var(--db-card-border) !important;
        }

        .net-kv-row {
          border-bottom: 1px solid var(--db-inner-border) !important;
        }

        .alert-row-item {
          background: var(--db-sub-card-bg) !important;
          border: 1px solid var(--db-inner-border) !important;
        }

        /* Tables styling overrides */
        thead tr {
          border-bottom: 1px solid var(--db-table-row-border) !important;
        }

        tbody tr {
          border-bottom: 1px solid var(--db-table-row-border) !important;
          transition: background 0.15s ease;
        }

        tbody tr:hover {
          background: var(--db-row-hover-bg) !important;
        }

        /* ForumRiskTable component styling overrides */
        .frt-scroll {
          scrollbar-color: var(--db-text-muted) transparent !important;
        }
        .frt-scroll::-webkit-scrollbar-thumb {
          background: var(--db-inner-border) !important;
        }
        .frt-thead-row {
          background: var(--db-sub-card-bg) !important;
          border-bottom: 1px solid var(--db-inner-border) !important;
        }
        .frt-th {
          color: var(--db-text-muted) !important;
        }
        .frt-row {
          border-bottom: 1px solid var(--db-inner-border) !important;
        }
        .frt-row:hover {
          background: var(--db-row-hover-bg) !important;
        }
        .frt-td-num {
          color: var(--db-text-muted) !important;
        }
        .frt-forum-name {
          color: var(--db-text-main) !important;
        }
        .frt-td-count {
          color: var(--db-text-subtle) !important;
        }
        .frt-fade-out {
          background: linear-gradient(180deg, transparent 0%, var(--db-card-bg) 100%) !important;
        }

        /* Sidebar Light Mode Overrides */
        body.light-theme .sidebar {
          background: #ffffff !important;
          border-right: 1px solid #cbd5e1 !important;
        }
        body.light-theme .logo-area {
          border-bottom: 1px solid #cbd5e1 !important;
        }
        body.light-theme .sidebar-footer {
          border-top: 1px solid #cbd5e1 !important;
        }
        body.light-theme .nav-item {
          color: #475569 !important;
        }
        body.light-theme .nav-item:hover {
          background: rgba(0, 0, 0, 0.04) !important;
          color: #0f172a !important;
        }
        body.light-theme .nav-item.active {
          background: rgba(0, 102, 224, 0.12) !important;
          color: #6366F1 !important;
          border-left: 3px solid #6366F1 !important;
        }
        body.light-theme .sidebar-section-label {
          color: #94a3b8 !important;
        }
        body.light-theme .nav-item-logout:hover {
          color: #ef4444 !important;
          background: rgba(239, 68, 68, 0.08) !important;
        }
      `}} />

      {/* ══ TOP HEADER ROW ════════════════════════════════════════════════ */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: "var(--db-text-main)", margin: 0, letterSpacing: "-0.02em" }}>Dashboard</h1>
          <p style={{ fontSize: 13, color: "var(--db-text-muted)", margin: "4px 0 0 0" }}>Here is today's report and performances</p>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Calendar dropdown */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--db-input-bg)", border: "1px solid var(--db-input-border)", borderRadius: 8, padding: "8px 14px", color: "var(--db-input-text)", fontSize: 13, cursor: "pointer" }}>
            <Calendar size={14} style={{ color: "var(--db-text-muted)" }} />
            <span style={{ fontWeight: 500 }}>Jun 1 - Jun 30</span>
          </div>

          {/* Timeframe selector */}
          <div
            onClick={() => setSelectedTimeframe(selectedTimeframe === "Monthly" ? "Weekly" : "Monthly")}
            style={{ display: "flex", alignItems: "center", gap: 6, background: "var(--db-input-bg)", border: "1px solid var(--db-input-border)", borderRadius: 8, padding: "8px 14px", color: "var(--db-input-text)", fontSize: 13, cursor: "pointer" }}
          >
            <span style={{ fontWeight: 500 }}>{selectedTimeframe}</span>
            <ChevronDown size={14} style={{ color: "var(--db-text-muted)" }} />
          </div>

          {/* Segment selector */}
          <div
            onClick={() => setSelectedSegment(selectedSegment === "All Segment" ? "Critical Only" : "All Segment")}
            style={{ display: "flex", alignItems: "center", gap: 6, background: "var(--db-input-bg)", border: "1px solid var(--db-input-border)", borderRadius: 8, padding: "8px 14px", color: "var(--db-input-text)", fontSize: 13, cursor: "pointer" }}
          >
            <span style={{ fontWeight: 500 }}>{selectedSegment}</span>
            <ChevronDown size={14} style={{ color: "var(--db-text-muted)" }} />
          </div>

          {/* User profile info block */}
          {user && (
            <div style={{ display: "flex", alignItems: "center", gap: "10px", background: "var(--db-input-bg)", border: "1px solid var(--db-input-border)", borderRadius: 8, padding: "6px 12px" }}>
              <div style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                background: "rgba(239, 68, 68, 0.2)",
                color: "#ef4444",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "11px",
                fontWeight: 800,
                lineHeight: "24px"
              }}>
                {user.email ? user.email.charAt(0).toUpperCase() : "A"}
              </div>
              <span style={{ fontSize: "12px", color: "var(--db-text-subtle)", fontWeight: 500 }}>{user.email}</span>
              <span style={{
                background: "rgba(239, 68, 68, 0.15)",
                color: "#ef4444",
                fontSize: "9px",
                fontWeight: 800,
                padding: "2px 6px",
                borderRadius: "4px",
                textTransform: "uppercase"
              }}>{user.role}</span>
            </div>
          )}

          {/* AI Assistant Button */}
          <button
            onClick={handleAIAssistant}
            style={{
              background: "linear-gradient(135deg, #a855f7 0%, #6366F1 100%)",
              border: "none",
              color: "#fff",
              padding: "9px 18px",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
              boxShadow: "0 4px 14px rgba(0, 102, 224, 0.3)",
            }}
          >
            <Sparkles size={14} />
            <span>AI Assistant</span>
          </button>
        </div>
      </div>

      {/* ══ ROW 1: FIVE STAT CARDS ═══════════════════════════════════════ */}
      <div className="stat-cards-row">
        {statCards.map((card, idx) => {
          let strokeColor = "#818cf8";
          let iconColor = "#818cf8";
          let iconBg = "rgba(129, 140, 248, 0.1)";
          let IconComp = FileText;
          let trend = "↑ 12.4%";
          
          if (card.title === "Total Leaks") {
            strokeColor = "#a855f7";
            iconColor = "#a855f7";
            iconBg = "rgba(168, 85, 247, 0.15)";
            IconComp = FileText;
            trend = "↑ 12.4%";
          } else if (card.title === "Hidden Leaks") {
            strokeColor = "#a855f7";
            iconColor = "#a855f7";
            iconBg = "rgba(168, 85, 247, 0.15)";
            IconComp = Lock;
            trend = "↑ 8.7%";
          } else if (card.title === "Visible Leaks") {
            strokeColor = "#3b82f6";
            iconColor = "#22d3ee";
            iconBg = "rgba(34, 211, 238, 0.15)";
            IconComp = Eye;
            trend = "↑ 15.2%";
          } else if (card.title === "Failed Jobs") {
            strokeColor = "#ef4444";
            iconColor = "#ef4444";
            iconBg = "rgba(239, 68, 68, 0.15)";
            IconComp = AlertTriangle;
            trend = "↓ 100%";
          } else if (card.title === "Retry Jobs") {
            strokeColor = "#f59e0b";
            iconColor = "#f59e0b";
            iconBg = "rgba(245, 158, 11, 0.15)";
            IconComp = Clock;
            trend = "↓ 100%";
          }

          const sparklinePoints: Record<string, string> = {
            "Total Leaks": "0,15 10,12 20,18 30,10 40,16 50,8 60,14 70,5 80,12 90,4 100,12",
            "Hidden Leaks": "0,16 10,10 20,15 30,8 40,12 50,5 60,11 70,3 80,9 90,6 100,10",
            "Visible Leaks": "0,18 10,14 20,16 30,11 40,13 50,9 60,15 70,10 80,12 90,5 100,8",
            "Failed Jobs": "0,15 10,17 20,14 30,16 40,13 50,15 60,12 70,14 80,11 90,13 100,15",
            "Retry Jobs": "0,12 10,14 20,10 30,13 40,9 50,11 60,8 70,10 80,7 90,9 100,12"
          };
          const pts = sparklinePoints[card.title] || "0,10 50,10 100,10";
          const pathData = `M ${pts.split(' ').map((p, i) => {
            const [x, y] = p.split(',');
            return `${i === 0 ? '' : 'L '}${x} ${y}`;
          }).join(' ')}`;

          return (
            <div key={idx} className="stat-card-mod">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <span style={{ fontSize: 12, color: "var(--db-text-muted)", fontWeight: 500 }}>{card.title}</span>
                <div className="stat-icon-wrap" style={{ background: iconBg, color: iconColor }}>
                  <IconComp size={14} />
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 4 }}>
                <span style={{ fontSize: 24, fontWeight: 700, color: "var(--db-text-main)" }}>
                  {card.value.toLocaleString()}
                </span>
                <span style={{ fontSize: 11, fontWeight: 600, color: trend.includes("↑") ? "#22c55e" : "#ef4444" }}>
                  {trend}
                </span>
              </div>
              <div style={{ fontSize: 11, color: "var(--db-text-muted)", marginTop: 2 }}>{card.subtitle}</div>
              
              <div style={{ height: 20, marginTop: 8, overflow: "hidden" }}>
                <svg width="100%" height="20" viewBox="0 0 100 20" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id={`grad-${card.title.replace(/\s+/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={strokeColor} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={strokeColor} stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d={pathData} fill="none" stroke={strokeColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d={`${pathData} L 100 20 L 0 20 Z`} fill={`url(#grad-${card.title.replace(/\s+/g, '')})`} />
                </svg>
              </div>
            </div>
          );
        })}
      </div>

      {/* ══ ROW 2: LEAK DISTRIBUTION & INGESTION PERFORMANCE ════════════ */}
      <div className="middle-grid">


        {/* Right Card: Leak Distribution */}
        <div className="db-card-modern" style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: "var(--db-text-main)" }}>Leak Distribution</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: "#22c55e", background: "rgba(34, 197, 94, 0.15)", padding: "2px 8px", borderRadius: 4 }}>• SYSTEM ONLINE</span>
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-around", margin: "16px 0" }}>
            <div style={{ position: "relative", width: 120, height: 120 }}>
              <svg width="120" height="120" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="45" fill="none" stroke="var(--db-thead-bg)" strokeWidth="12" />
                {distributionData.list.map((c, idx) => (
                  <circle
                    key={idx}
                    cx="60"
                    cy="60"
                    r="45"
                    fill="none"
                    stroke={c.color}
                    strokeWidth="12"
                    strokeDasharray={c.dashArray}
                    strokeDashoffset={c.dashOffset}
                    transform="rotate(-90 60 60)"
                  />
                ))}
              </svg>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: "var(--db-donut-center-text)" }}>
                  {stats.total_leaks.toLocaleString()}
                </span>
                <span style={{ fontSize: 9, color: "var(--db-donut-center-subtext)" }}>Total Leaks</span>
              </div>
            </div>

            <div className="dist-legend-list">
              {distributionData.list.map((c, idx) => (
                <div key={idx} style={{ display: "flex", alignItems: "center", justifyItems: "space-between", gap: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, width: 85 }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: c.color }} />
                    <span style={{ fontSize: 11, color: "var(--db-text-subtle)" }}>{c.label}</span>
                  </div>
                  <span style={{ fontSize: 11, color: "var(--db-text-muted)", width: 35, textAlign: "right" }}>{c.pct.toFixed(1)}%</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: "var(--db-text-main)", width: 40, textAlign: "right" }}>{c.count.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          <button
            style={{
              width: "100%",
              background: "none",
              border: "1px solid var(--db-input-border)",
              borderRadius: 8,
              padding: "10px",
              color: "var(--db-text-subtle)",
              fontSize: 12,
              fontWeight: 500,
              cursor: "pointer",
            }}
            onClick={() => toast.info("Comprehensive leak report details loaded in background.")}
          >
            View Full Report
          </button>
        </div>


        {/* Col 1: Ingestion Performance */}
        <div className="db-card-modern" style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <span style={{ fontSize: 14, fontWeight: 600, color: "var(--db-text-main)" }}>Ingestion Performance</span>
              <span style={{ fontSize: 10, color: "var(--db-text-muted)", marginLeft: 6 }}>(Top Forums Ingestion Speed)</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4, background: "var(--db-input-bg)", border: "1px solid var(--db-input-border)", borderRadius: 6, padding: "4px 8px", color: "var(--db-input-text)", fontSize: 10, cursor: "pointer" }}>
              <span>Weekly</span>
              <ChevronDown size={10} />
            </div>
          </div>

          <div className="chart-bar-list">
            {[
              { name: "BreachForums", val: "2.4M", pct: 100 },
              { name: "Exploit.in", val: "1.8M", pct: 75 },
              { name: "XSS.is", val: "1.2M", pct: 50 },
              { name: "RussianMarket", val: "920K", pct: 38 },
              { name: "RuTor", val: "688K", pct: 28 },
              { name: "Ramp", val: "430K", pct: 18 },
              { name: "CookieShop", val: "310K", pct: 12 }
            ].map((bar, idx) => (
              <div key={idx} className="chart-bar-item">
                <span className="chart-bar-text-val" style={{ color: "#a855f7" }}>{bar.val}</span>
                <div className="chart-bar-rect-wrap" style={{
                  height: `${bar.pct * 0.9}px`,
                  width: "16px",
                  background: "linear-gradient(180deg, #7c3aed 0%, rgba(124, 58, 237, 0.25) 100%)"
                }} />
                <span style={{ fontSize: 8, color: "var(--db-text-muted)", width: 45, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "center" }}>{bar.name}</span>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* ══ ROW 3: THREE COLUMNS (QUEUE, ALERTS, RECENT ACTIVITIES) ══════════════ */}
      <div className="bottom-three-column-grid">
        {/* Col 2: Queue & Infrastructure */}
        <div className="db-card-modern" style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--db-text-main)" }}>Queue &amp; Infrastructure</span>
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-around", margin: "10px 0" }}>
            <div style={{ position: "relative", width: 80, height: 80 }}>
              <svg width="80" height="80" viewBox="0 0 80 80">
                <circle cx="40" cy="40" r="32" fill="none" stroke="var(--db-thead-bg)" strokeWidth="8" />
                <circle cx="40" cy="40" r="32" fill="none" stroke="#6366F1" strokeWidth="8" strokeDasharray="160 200" strokeDashoffset="0" />
                <circle cx="68" cy="24" r="5" fill="#eab308" />
              </svg>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: "var(--db-donut-center-text)" }}>0</span>
                <span style={{ fontSize: 7, color: "var(--db-donut-center-subtext)" }}>QUEUE SIZE</span>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", justifyItems: "space-between", alignItems: "center", gap: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, width: 110 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#6366F1" }} />
                  <span style={{ fontSize: 11, color: "var(--db-text-subtle)" }}>Pending approvals</span>
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, color: "var(--db-text-main)" }}>0</span>
              </div>
              <div style={{ display: "flex", justifyItems: "space-between", alignItems: "center", gap: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, width: 110 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e" }} />
                  <span style={{ fontSize: 11, color: "var(--db-text-subtle)" }}>Active remediation</span>
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, color: "var(--db-text-main)" }}>0</span>
              </div>
              <div style={{ display: "flex", justifyItems: "space-between", alignItems: "center", gap: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, width: 110 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#eab308" }} />
                  <span style={{ fontSize: 11, color: "var(--db-text-subtle)" }}>Tor exit status</span>
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#ef4444" }}>Offline</span>
              </div>
              <div style={{ display: "flex", justifyItems: "space-between", alignItems: "center", gap: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, width: 110 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#6b7280" }} />
                  <span style={{ fontSize: 11, color: "var(--db-text-subtle)" }}>Clear IP status</span>
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#22c55e" }}>Secure</span>
              </div>
            </div>
          </div>

          <button
            style={{
              width: "100%",
              background: "none",
              border: "1px solid var(--db-input-border)",
              borderRadius: 8,
              padding: "8px",
              color: "var(--db-text-subtle)",
              fontSize: 12,
              fontWeight: 500,
              cursor: "pointer",
            }}
            onClick={() => toast.info("No active queues are currently blocked.")}
          >
            View Network Details
          </button>
        </div>

        {/* Col 3: Recent Alerts */}
        <div className="db-card-modern" style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--db-text-main)" }}>Recent Alerts</span>
            <button style={{ background: "none", border: "none", color: "var(--db-text-muted)", fontSize: 11, cursor: "pointer" }}>View All</button>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
            {[
              { desc: "New database leak detected", time: "2 min ago", priority: "High Priority", bg: "rgba(239, 68, 68, 0.1)", color: "#f97316", border: "rgba(249, 115, 22, 0.3)" },
              { desc: "Tor exit node changed", time: "15 min ago", priority: "Medium", bg: "rgba(245, 158, 11, 0.1)", color: "#f59e0b", border: "rgba(245, 158, 11, 0.3)" },
              { desc: "Credential dump uploaded", time: "32 min ago", priority: "High Priority", bg: "rgba(239, 68, 68, 0.1)", color: "#f97316", border: "rgba(249, 115, 22, 0.3)" },
              { desc: "Mass data exposure detected", time: "1 hour ago", priority: "Critical", bg: "rgba(239, 68, 68, 0.2)", color: "#ef4444", border: "rgba(239, 68, 68, 0.5)" }
            ].map((alert, idx) => (
              <div key={idx} className="alert-row-item" style={{ padding: "8px 10px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <AlertTriangle size={13} style={{ color: alert.color }} />
                  <div>
                    <div style={{ color: "var(--db-text-main)", fontWeight: 500, fontSize: 11 }}>{alert.desc}</div>
                    <div style={{ color: "var(--db-text-muted)", fontSize: 9, marginTop: 1 }}>{alert.time}</div>
                  </div>
                </div>
                <span style={{
                  background: alert.bg,
                  color: alert.color,
                  border: `1px solid ${alert.border}`,
                  padding: "1px 6px",
                  borderRadius: 4,
                  fontSize: 9,
                  fontWeight: 700
                }}>{alert.priority}</span>
              </div>
            ))}
          </div>
        </div>


        {/* Col 3: Recent Activities */}
      {/* ══ ROW 4: RECENT ACTIVITIES ═════════════════════════════════════ */}
      <div className="db-card-modern">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, color: "var(--db-text-main)", margin: 0 }}>Recent Activities</h2>
          <button style={{ background: "none", border: "1px solid var(--db-input-border)", borderRadius: 6, padding: "5px 12px", fontSize: 11, color: "var(--db-text-subtle)", cursor: "pointer" }}>
            View All Activities
          </button>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--db-table-row-border)", color: "var(--db-text-muted)", fontWeight: 500 }}>
              <th style={{ padding: "10px 12px" }}>Time</th>
              <th style={{ padding: "10px 12px" }}>Event</th>
              <th style={{ padding: "10px 12px" }}>Source</th>
              <th style={{ padding: "10px 12px" }}>Severity</th>
              <th style={{ padding: "10px 12px" }}>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: "1px solid var(--db-table-row-border)" }}>
              <td style={{ padding: "12px 12px", color: "var(--db-text-muted)" }}>2 min ago</td>
              <td style={{ padding: "12px 12px", color: "var(--db-text-main)", fontWeight: 500 }}>Database leak detected</td>
              <td style={{ padding: "12px 12px", color: "var(--db-text-subtle)" }}>BreachForums</td>
              <td style={{ padding: "12px 12px" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "#ef4444" }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#ef4444" }} />
                  High
                </span>
              </td>
              <td style={{ padding: "12px 12px" }}>
                <span style={{ background: "rgba(168, 85, 247, 0.15)", color: "#a855f7", padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>Open</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      </div>

      {/* ══ ROW 5: ENGAGEMENT QUEUE TABLE (Preserves full original functionality) ══ */}
      <div className="db-card-modern">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <h2 style={{ fontSize: 15, fontWeight: 600, color: "var(--db-text-main)", margin: 0 }}>Engagement Queue</h2>
            
            {/* Tabs */}
            <div style={{ display: "flex", gap: 4, background: "var(--db-input-bg)", padding: 3, borderRadius: 6, border: "1px solid var(--db-input-border)" }}>
              <button
                onClick={() => setEqTab("pending")}
                style={{
                  background: eqTab === "pending" ? "var(--db-tab-active-bg)" : "none",
                  border: "none",
                  borderRadius: 4,
                  padding: "4px 10px",
                  fontSize: 11,
                  fontWeight: eqTab === "pending" ? 600 : 500,
                  color: eqTab === "pending" ? "var(--db-tab-active-text)" : "var(--db-tab-inactive-text)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 4
                }}
              >
                <Clock size={11} />
                <span>Pending Approval</span>
                {pendingLeaks.length > 0 && (
                  <span style={{ fontSize: 9, background: "#6366F1", color: "#fff", padding: "1px 5px", borderRadius: 999 }}>
                    {pendingLeaks.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setEqTab("active")}
                style={{
                  background: eqTab === "active" ? "var(--db-tab-active-bg)" : "none",
                  border: "none",
                  borderRadius: 4,
                  padding: "4px 10px",
                  fontSize: 11,
                  fontWeight: eqTab === "active" ? 600 : 500,
                  color: eqTab === "active" ? "var(--db-tab-active-text)" : "var(--db-tab-inactive-text)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 4
                }}
              >
                <Zap size={11} />
                <span>Active Remediation</span>
                {activeLeaks.length > 0 && (
                  <span style={{ fontSize: 9, background: "#22c55e", color: "#fff", padding: "1px 5px", borderRadius: 999 }}>
                    {activeLeaks.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Search Box */}
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <Search size={12} style={{ position: "absolute", left: 10, color: "var(--db-text-muted)" }} />
              <input
                type="text"
                placeholder="Search queue..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                style={{
                  background: "var(--db-input-bg)",
                  border: "1px solid var(--db-input-border)",
                  borderRadius: 6,
                  padding: "4px 8px 4px 28px",
                  fontSize: 11,
                  color: "var(--db-input-text)",
                  width: 140,
                  outline: "none"
                }}
              />
            </div>

            <div
              onClick={() => setSelectedClientFilter(selectedClientFilter === "All active" ? "Critical Only" : "All active")}
              style={{ display: "flex", alignItems: "center", gap: 6, background: "var(--db-input-bg)", border: "1px solid var(--db-input-border)", borderRadius: 6, padding: "5px 10px", color: "var(--db-input-text)", fontSize: 11, cursor: "pointer" }}
            >
              <span>{selectedClientFilter}</span>
              <ChevronDown size={12} style={{ color: "var(--db-text-muted)" }} />
            </div>
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          {filteredLeaks.length === 0 ? (
            <div style={{ padding: "30px", textAlign: "center", color: "var(--db-text-muted)", fontSize: 12 }}>
              No leaks in this queue stage.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--db-table-row-border)", color: "var(--db-text-muted)", fontWeight: 500 }}>
                  <th style={{ padding: "10px 12px" }}>ID</th>
                  <th style={{ padding: "10px 12px" }}>Client Name</th>
                  <th style={{ padding: "10px 12px" }}>Source Forum</th>
                  <th style={{ padding: "10px 12px" }}>Severity</th>
                  <th style={{ padding: "10px 12px" }}>Asset Classification</th>
                  <th style={{ padding: "10px 12px" }}>Network Layer</th>
                  <th style={{ padding: "10px 12px", textAlign: "right" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeaks.map((leak) => {
                  const initials = (leak.client_name || "Client").split(" ").map(n => n[0]).join("");
                  return (
                    <tr key={leak._id} style={{ borderBottom: "1px solid var(--db-table-row-border)" }}>
                      <td style={{ padding: "12px", color: "var(--db-text-muted)", fontFamily: "monospace" }}>
                        #LK{leak._id.substring(leak._id.length - 4).toUpperCase()}
                      </td>
                      <td style={{ padding: "12px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 26, height: 26, borderRadius: "50%", background: "rgba(129, 140, 248, 0.15)", color: "#818cf8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600 }}>
                            {initials}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, color: "var(--db-text-main)" }}>{leak.client_name || "Unknown"}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: "12px", color: "var(--db-text-main)" }}>{leak.forum || "—"}</td>
                      <td style={{ padding: "12px" }}>
                        <span style={{
                          padding: "2px 6px",
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 700,
                          background: leak.severity === "Critical" ? "rgba(239, 68, 68, 0.15)" : leak.severity === "High" ? "rgba(249, 115, 22, 0.15)" : "rgba(59, 130, 246, 0.15)",
                          color: leak.severity === "Critical" ? "#ef4444" : leak.severity === "High" ? "#f97316" : "#3b82f6"
                        }}>
                          {leak.severity || "Medium"}
                        </span>
                      </td>
                      <td style={{ padding: "12px", color: "var(--db-text-subtle)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {leak.thread_title || leak.title || "—"}
                      </td>
                      <td style={{ padding: "12px", color: "var(--db-text-muted)" }}>{leak.network || "Tor Network"}</td>
                      <td style={{ padding: "12px", textAlign: "right" }}>
                        {eqTab === "pending" ? (
                          <button
                            style={{
                              background: "rgba(245, 158, 11, 0.1)",
                              border: "1px solid rgba(245, 158, 11, 0.2)",
                              borderRadius: 4,
                              padding: "4px 8px",
                              fontSize: "10px",
                              color: "#f59e0b",
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4
                            }}
                            onClick={() => toast.success(`Reminder notification sent to ${leak.client_name || "Client"}`)}
                          >
                            <Bell size={11} />
                            Send Reminder
                          </button>
                        ) : (
                          <button
                            style={{
                              background: "var(--db-input-bg)",
                              border: "1px solid var(--db-input-border)",
                              borderRadius: 4,
                              padding: "4px 8px",
                              fontSize: "10px",
                              color: "var(--db-input-text)",
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4
                            }}
                            onClick={() => toast.info(`Active engagement started on: ${leak.engagement_started_at ? new Date(leak.engagement_started_at).toLocaleDateString() : "—"}`)}
                          >
                            <Info size={11} />
                            Details
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
      
      {/* ══ ROW 6: FORUM RISK ANALYTICS ═══════════════════════════════════ */}
      {forumRisk.length > 0 && (
        <div className="db-card-modern">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Lock size={15} style={{ color: "#a855f7" }} />
              <h2 style={{ fontSize: 15, fontWeight: 600, color: "var(--db-text-main)", margin: 0 }}>Forum Risk Analytics</h2>
            </div>
            <span style={{ fontSize: 10, background: "var(--db-input-bg)", border: "1px solid var(--db-input-border)", padding: "2px 8px", borderRadius: 4, color: "var(--db-text-subtle)" }}>
              {forumRisk.length} forum{forumRisk.length !== 1 ? "s" : ""} tracked
            </span>
          </div>
          <ForumRiskTable data={forumRisk} />
        </div>
      )}
      <ScraperForumStatus />

      {/* ══ NETWORK INFRASTRUCTURE — MOVED TO BOTTOM ═══════════════════ */}
        <div className="db-card-modern">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Activity size={16} style={{ color: "#3b82f6" }} />
              <span style={{ fontSize: 15, fontWeight: 600, color: "var(--db-text-main)" }}>Network Infrastructure</span>
            </div>
            <button style={{ background: "none", border: "none", color: "var(--db-text-muted)", fontSize: 11, cursor: "pointer" }}>View All</button>
          </div>

          <div className="network-content-layout">
            {/* Card 1: Public IP Access */}
            <div className="net-sub-card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Globe size={16} style={{ color: "#3b82f6" }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>Public IP Access</div>
                    <div style={{ fontSize: 10, color: "var(--db-text-muted)" }}>STANDARD GATEWAY</div>
                  </div>
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#22c55e", background: "rgba(34, 197, 94, 0.15)", padding: "2px 8px", borderRadius: 4 }}>CLEAR</span>
              </div>

              <div style={{ margin: "14px 0" }}>
                <div className="net-kv-row">
                  <span style={{ color: "var(--db-text-muted)" }}>IPv4 Address</span>
                  <span style={{ color: "var(--db-text-main)", display: "flex", alignItems: "center", gap: 6, fontWeight: 500 }}>
                    {publicIP?.ip || "103.89.163.227"}
                    <Copy size={12} style={{ color: "var(--db-text-muted)", cursor: "pointer" }} onClick={() => handleCopyIP(publicIP?.ip || "103.89.163.227")} />
                  </span>
                </div>
                <div className="net-kv-row">
                  <span style={{ color: "var(--db-text-muted)" }}>Geolocation</span>
                  <span style={{ color: "var(--db-text-main)", display: "flex", alignItems: "center", gap: 4 }}>
                    <MapPin size={12} style={{ color: "#ef4444" }} />
                    {publicIP?.city ? `${publicIP.city}, ${publicIP.country}` : "Pune, India"}
                  </span>
                </div>
                <div className="net-kv-row">
                  <span style={{ color: "var(--db-text-muted)" }}>ISP Provider</span>
                  <span style={{ color: "var(--db-text-main)", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {publicIP?.isp || "Rajesh Digital And Datacom Pvt Ltd"}
                  </span>
                </div>
                <div className="net-kv-row">
                  <span style={{ color: "var(--db-text-muted)" }}>ASN Identifier</span>
                  <span style={{ color: "var(--db-text-main)", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {publicIP?.asn || "AS134674 TATA PLAY BROAD"}
                  </span>
                </div>
              </div>

              {/* Sparkline wave */}
              <div style={{ height: 16 }}>
                <svg width="100%" height="16" viewBox="0 0 100 20" preserveAspectRatio="none">
                  <path d="M 0,15 L 10,12 L 20,17 L 30,11 L 40,16 L 50,9 L 60,14 L 70,8 L 80,13 L 90,7 L 100,12" fill="none" stroke="#6366F1" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
            </div>

            {/* Card 2: Tor Exit Node */}
            <div className="net-sub-card" style={{ alignItems: "center", justifyContent: "center" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Lock size={16} style={{ color: "#a855f7" }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>Tor Exit Node</div>
                    <div style={{ fontSize: 10, color: "var(--db-text-muted)" }}>ONION OVERLAY</div>
                  </div>
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#a855f7", background: "rgba(168, 85, 247, 0.15)", padding: "2px 8px", borderRadius: 4 }}>ONION</span>
              </div>
              
              <div className="tunnel-circle-container" style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                <div className="tunnel-circle-ring">
                  <div className="tunnel-circle-ring-inner">
                    <Lock size={20} style={{ color: "#a855f7" }} />
                  </div>
                </div>
                <span style={{ color: "#a855f7", fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", marginTop: 4 }}>ESTABLISHING TUNNEL</span>
                {torIP?.ip && (
                  <span style={{ fontSize: 9, color: "var(--db-text-muted)", marginTop: 2 }}>Node: {torIP.ip}</span>
                )}
                <div className="blinking-dots" style={{ marginTop: 6, display: "flex", gap: 4 }}>
                  <span className="pulse-dot" />
                  <span className="pulse-dot" />
                  <span className="pulse-dot" />
                </div>
              </div>
            </div>
          </div>
        </div>

    </div>
  );
}
